import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Database, RefreshCw, ChevronDown, ChevronRight, Download, FilterX, DatabaseZap, AlertTriangle, Sigma } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import CoinMHistory from '../components/CoinMHistory';
import { getTheme } from '../App';
import { useWS } from '../WSContext';

// ─── Tipler ───────────────────────────────────────────────────────────────────

interface HistoryTrade {
  id: string; bot_id: string; symbol: string; side: string;
  price: number; quantity: number; realizedPnl: number;
  fee: number; gasFee?: number; executedAtMillis: number;
}

interface HistoryFunding {
  id: string; bot_id: string; symbol: string;
  fundingFee: number; paymentType: string; createdTime: number;
}

interface Props { dark?: boolean; }

// ─── Yardımcılar ──────────────────────────────────────────────────────────────

/** Fiyat ondalığı pariteye göre değişmeli: BTC 86.000 ile STONK 0,134 aynı
 *  biçimde okunmuyor. Sabit 4 hane birinde gürültü, diğerinde bilgi kaybı. */
function fmtAvgPrice(v: number | null): string {
  if (v === null || !isFinite(v)) return '—';
  const d = v >= 1000 ? 2 : v >= 1 ? 4 : 6;
  return '$' + v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
}
/** Miktar: 44.800 MON ile 0,0007 BTC aynı ölçekte değil. */
function fmtAvgQty(v: number): string {
  if (!isFinite(v)) return '—';
  const d = v >= 1000 ? 0 : v >= 1 ? 3 : 6;
  return v.toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtSym(s: string) {
  return (s || '').replace('PERP_','').replace('_USDC','').replace('-PERP','');
}

function fmtBot(id: string) {
  if (!id) return '—';
  if (id === 'orderly-main')      return 'Orderly';
  if (id === 'deepblue-main')     return 'DeepBlue';
  if (id === 'dydx-main')         return 'dYdX';
  if (id === 'aster1')            return 'AsterDex';
  if (id === 'binance1')          return 'Binance';
  return id;
}

/** BUY veya LONG → true, SELL veya SHORT → false */
function isBuySide(side: string) {
  return side === 'BUY' || side === 'LONG';
}

/** bot_id → kısa borsa adı (sembol etiketinde parantez içi için) */
function botToExchange(bot_id: string): string {
  const id = (bot_id || '').toLowerCase();
  if (id.includes('backpack') || id.includes('bluefin') || id === 'bot2') return 'Bluefin';
  if (id.includes('orderly')  || id.includes('raydium') || id === 'bot1') return 'Orderly';
  if (id.includes('vera')     || id === 'vera')                           return 'Vera';
  if (id.includes('deepblue') || id === 'deepblue-main')                  return 'DeepBlue';
  if (id.includes('hyperliquid'))                                          return 'HL';
  if (id.includes('dydx')     || id === 'dydx-main')                      return 'dYdX';
  if (id.includes('apex')     || id === 'apex-main')                      return 'Apex';
  if (id.includes('aster')    || id === 'aster1')                          return 'AsterDex';
  if (id.includes('binance')  || id === 'binance1')                        return 'Binance';
  return fmtBot(bot_id);
}

function fmtDate(ms: number | null | undefined) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('tr-TR');
}

function fmtTime(ms: number | null | undefined) {
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString('tr-TR');
}

// msToDateInput: milliseconds → "YYYY-MM-DD" for <input type="date"> (yerel tarih)
function msToDateInput(ms: number) {
  const d = new Date(ms);
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ─── CSV export ────────────────────────────────────────────────────────────────

function exportXls(rows: any[][], filename: string) {
  const esc = (v: string) =>
    v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const xmlRows = rows.map(row =>
    '<Row>' + row.map(cell => {
      const v = cell == null ? '' : String(cell);
      const isNum = v !== '' && !isNaN(Number(v));
      return isNum
        ? `<Cell><Data ss:Type="Number">${v}</Data></Cell>`
        : `<Cell><Data ss:Type="String">${esc(v)}</Data></Cell>`;
    }).join('') + '</Row>'
  ).join('');

  const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Sayfa1"><Table>${xmlRows}</Table></Worksheet></Workbook>`;

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename + '.xls';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sembol çoklu seçim dropdown ─────────────────────────────────────────────

interface SymbolMultiSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (v: string[]) => void;
}

function SymbolMultiSelect({ options, selected, onChange }: SymbolMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (val: string) => {
    if (selected.includes(val)) onChange(selected.filter(s => s !== val));
    else onChange([...selected, val]);
  };

  const label = selected.length === 0
    ? 'Tümü'
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} sembol`;

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between gap-1 border rounded px-1.5 py-0.5 text-[10px] transition-colors cursor-pointer
          ${selected.length > 0
            ? 'border-[#4A9FE8]/70 text-[#4A9FE8] bg-[#4A9FE8]/5'
            : 'border-[#0a2560] text-blue-300 hover:border-blue-500/60'
          }`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={10} className="flex-shrink-0 opacity-60" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-[#0d0a1f] border border-[#0a2560] rounded-lg shadow-2xl min-w-[160px] max-h-60 overflow-y-auto">
          {/* Tümü */}
          <button
            onClick={() => { onChange([]); setOpen(false); }}
            className={`w-full text-left px-3 py-1.5 text-[10px] flex items-center gap-2 transition-colors
              ${selected.length === 0 ? 'text-[#4A9FE8]' : 'text-blue-300 hover:bg-[#1a0e38] hover:text-white'}`}
          >
            <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0
              ${selected.length === 0 ? 'border-[#4A9FE8] bg-[#4A9FE8]' : 'border-blue-600'}`}>
              {selected.length === 0 && <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#061530" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </span>
            Tümü
          </button>
          <div className="border-t border-[#0a2560]/60 my-0.5" />
          {options.map(o => {
            const checked = selected.includes(o.value);
            return (
              <button
                key={o.value}
                onClick={() => toggle(o.value)}
                className={`w-full text-left px-3 py-1.5 text-[10px] flex items-center gap-2 transition-colors
                  ${checked ? 'text-[#4A9FE8]' : 'text-blue-300 hover:bg-[#1a0e38] hover:text-white'}`}
              >
                <span className={`w-3 h-3 rounded-sm border flex items-center justify-center flex-shrink-0
                  ${checked ? 'border-[#4A9FE8] bg-[#4A9FE8]' : 'border-blue-600'}`}>
                  {checked && <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#061530" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Filtre satırı input stili ────────────────────────────────────────────────

const FLT = 'w-full bg-transparent border border-[#0a2560] rounded px-1.5 py-0.5 text-[10px] text-blue-300 focus:outline-none focus:border-[#4A9FE8] placeholder-blue-700';

// Seçim anahtarı. Saf fonksiyon olduğu için bileşen DIŞINDA: içeride tanımlıysa
// her render'da yeni bir referans oluyor ve useMemo/React.memo bağımlılıklarını
// gereksiz yere geçersiz kılıyordu.
const selKey = (botId: string, id: string) => `${botId}|${id}`;

// ─── Tek işlem satırı ─────────────────────────────────────────────────────────
// React.memo ŞART: eskiden bir onay kutusuna tıklamak `selected`'ı yeni bir Set
// yapıyor, bu da 31.874 satırın tamamını yeniden uzlaştırıyordu. Satıra Set
// yerine hazır `sel` boolean'ı ve `tag` sayısı geçince yalnız DEĞİŞEN satır
// yeniden çiziliyor. Handler'lar üst bileşende useCallback ile sabitlendi.
interface TradeRowProps {
  r: HistoryTrade;
  idx: number;
  sel: boolean;
  tag: number;
  t: any;
  onRowClick:  (e: React.MouseEvent, idx: number, botId: string, id: string) => void;
  onBoxClick:  (e: React.MouseEvent, idx: number) => void;
  onBoxChange: (idx: number, botId: string, id: string) => void;
}

const TradeRow = React.memo(function TradeRow(
  { r, idx, sel, tag, t, onRowClick, onBoxClick, onBoxChange }: TradeRowProps,
) {
  return (
    <tr
      onMouseDown={e => { if (e.shiftKey) e.preventDefault(); }}
      onClick={e => onRowClick(e, idx, r.bot_id, r.id)}
      className={`border-b cursor-pointer select-none ${t.tableRow} ${t.cardBorder} ${sel ? 'bg-sky-500/10' : ''}`}>
      <td className="px-2 py-2">
        {/* Kutu satırın kendi handler'ını tetiklemesin — çift toggle olurdu */}
        <input type="checkbox" className="accent-sky-500"
          checked={sel}
          onClick={e => onBoxClick(e, idx)}
          onChange={() => onBoxChange(idx, r.bot_id, r.id)} />
      </td>
      <td className="px-2 py-2">
        {tag > 0 && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-sky-500/15 text-sky-400 border border-sky-500/30">
            G{tag}
          </span>
        )}
      </td>
      <td className={`px-3 py-2 ${t.muted}`}>{fmtDate(r.executedAtMillis)}</td>
      <td className={`px-3 py-2 ${t.muted}`}>{fmtTime(r.executedAtMillis)}</td>
      <td className="px-3 py-2">
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#0a2560] text-blue-300">
          {fmtBot(r.bot_id)}
        </span>
      </td>
      <td className={`px-3 py-2 font-semibold ${t.gold}`}>{fmtSym(r.symbol)}</td>
      <td className="px-3 py-2">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
          isBuySide(r.side) ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
        }`}>{r.side}</span>
      </td>
      <td className={`px-3 py-2 text-right font-mono ${t.text}`}>${(r.price || 0).toFixed(4)}</td>
      <td className={`px-3 py-2 text-right font-mono ${t.text}`}>{r.quantity}</td>
      <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
        ${((r.price || 0) * (r.quantity || 0)).toFixed(2)}
      </td>
      <td className={`px-3 py-2 text-right font-mono font-bold ${(r.realizedPnl || 0) >= 0 ? t.positive : t.negative}`}>
        {(r.realizedPnl || 0) >= 0 ? '+' : ''}${(r.realizedPnl || 0).toFixed(4)}
      </td>
      <td className={`px-3 py-2 text-right font-mono ${t.negative}`}>
        -${(r.fee || 0).toFixed(6)}
      </td>
    </tr>
  );
});

// ─── Bileşen ──────────────────────────────────────────────────────────────────

export default function SQLiteHistory({ dark = true }: Props) {
  const { connected, sendWs, subscribe, isMainBackend } = useWS() as any;
  const [fetchError, setFetchError] = useState<'' | 'backend' | 'timeout'>('');
  // getTheme her çağrıda YENİ nesne döndürüyor. Memoize edilmezse satırlara
  // geçirilen `t` her render'da değişir ve React.memo hiç tutmaz.
  const t = useMemo(() => getTheme(dark), [dark]);

  // Ham veri
  const [allTrades,  setTrades]  = useState<HistoryTrade[]>([]);
  const [allFunding, setFunding] = useState<HistoryFunding[]>([]);
  // COIN-M satırları (bot_id binance1-cm) USD görünümünden AYRI tutulur: bu sayfanın
  // tüm toplamları $ ile yazılıyor, ETH cinsinden PnL/ücret/funding oraya karışırdı.
  // Aşağıdaki mantığın tamamı `trades`/`funding` üzerinden çalıştığı için USD
  // görünümü bu iki türetilmiş listeyle otomatik olarak COIN-M'siz kalıyor.
  const CM_BOT = 'binance1-cm';
  const trades    = useMemo(() => allTrades.filter(r => r.bot_id !== CM_BOT), [allTrades]);
  const funding   = useMemo(() => allFunding.filter(r => r.bot_id !== CM_BOT), [allFunding]);
  const cmTrades  = useMemo(() => allTrades.filter(r => r.bot_id === CM_BOT), [allTrades]);
  const cmFunding = useMemo(() => allFunding.filter(r => r.bot_id === CM_BOT), [allFunding]);
  const [hTab, setHTab] = useState<'usd' | 'coinm'>('usd');
  const [cmGrids, setCmGrids] = useState<any[]>([]);   // kapanan COIN-M grid'leri
  const [loading, setLoading] = useState(false);

  // Filtreler
  const [dateFrom,   setDateFrom]   = useState('');        // YYYY-MM-DD
  const [dateTo,     setDateTo]     = useState('');
  const [botFilter,  setBotFilter]  = useState('all');
  const [symFilter,  setSymFilter]  = useState<string[]>([]);
  const [sideFilter, setSideFilter] = useState('all');     // trades only
  const [tab,        setTab]        = useState<'trades' | 'funding'>('trades');

  const [backfilling, setBackfilling] = useState(false);

  // Bölüm açık/kapalı
  const [tradesOpen,  setTradesOpen]  = useState(true);
  // Ortalama paneli: MODAL DEĞİL, şeridin altında açılan satır. Modal arkadaki
  // tabloyu kapatır ve her filtre denemesinde kapat-aç gerektirirdi; burada açık
  // kalıyor ve filtre değiştikçe canlı güncelleniyor.
  const [showAvg, setShowAvg] = useState(false);
  const [fundingOpen, setFundingOpen] = useState(true);

  // ── Funding arbitrage: trade → LP delta grubu işaretleme ──
  const [selected, setSelected] = useState<Set<string>>(new Set());   // "botId|tradeId"
  const [tradeTags, setTradeTags] = useState<Record<string, Record<string, number>>>({});
  const [tagOpen, setTagOpen] = useState(false);
  const [lpGroups, setLpGroups] = useState<{ no: number; kind: string; status: string; positions: number; trades: number; chains?: Record<string, number>; wallet?: string; rank?: number }[]>([]);
  const [onlyUngrouped, setOnlyUngrouped] = useState(false);   // gruplanmamışları göster
  const lastIdxRef = React.useRef<number | null>(null);         // shift-click aralık çapası
  const tagOf = (botId: string, id: string) => tradeTags[botId]?.[String(id)] || 0;

  // Handler'lar KARARLI olmalı, yoksa React.memo'lu satırlar her render'da
  // yeniden çizilir. setSelected'in fonksiyonel biçimi sayesinde bağımlılık yok.
  const toggleSel = useCallback((botId: string, id: string) => setSelected(prev => {
    const n = new Set(prev); const k = selKey(botId, id);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  }), []);

  // Verilen seçim anahtarlarını bir gruba ata (0 = bağı kaldır). Bot bazında gruplanır.
  // `selected` yerine AÇIK anahtar listesi alıyor: yeni grup akışı seçimi sunucu
  // cevabı gelene kadar taşımak zorunda, o an state çoktan temizlenmiş oluyor.
  const assignKeys = useCallback((groupNo: number, keys: string[]) => {
    if (keys.length === 0) return;
    const byBot: Record<string, string[]> = {};
    keys.forEach(k => { const [b, id] = k.split('|'); (byBot[b] ||= []).push(id); });
    Object.entries(byBot).forEach(([botId, ids]) =>
      sendWs({ type: 'set_lp_group_trades', payload: { botId, tradeIds: ids, groupNo } }));
    // optimistic
    setTradeTags(prev => {
      const n = { ...prev };
      Object.entries(byBot).forEach(([botId, ids]) => {
        n[botId] = { ...(n[botId] || {}) };
        ids.forEach(id => { if (groupNo > 0) n[botId][id] = groupNo; else delete n[botId][id]; });
      });
      return n;
    });
    setSelected(new Set());
    setTagOpen(false);
  }, [sendWs]);

  const assignGroup = useCallback((groupNo: number) =>
    assignKeys(groupNo, Array.from(selected)), [assignKeys, selected]);

  // "Yeni gruba ata" İKİ ADIM: numarayı sunucu üretiyor (create_lp_group →
  // lp_group_created), atama ancak cevap gelince yapılabiliyor. Seçim ref'te
  // taşınıyor — abonelik closure'ı [subscribe, fetchData] ile kurulduğu için
  // `selected`in ilk render'daki (çoğu zaman boş) hâlini yakalar.
  const pendingNewGroupRef = useRef<string[] | null>(null);
  const assignKeysRef = useRef(assignKeys);
  useEffect(() => { assignKeysRef.current = assignKeys; }, [assignKeys]);

  const assignToNewGroup = useCallback(() => {
    if (selected.size === 0) return;
    pendingNewGroupRef.current = Array.from(selected);
    setTagOpen(false);
    sendWs({ type: 'create_lp_group', payload: { kind: 'perp', name: '' } });
  }, [selected, sendWs]);

  // Arka arkaya gelen trades_updated mesajlarını tek çekimde birleştiren zamanlayıcı
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (refetchTimer.current) clearTimeout(refetchTimer.current); }, []);

  // Cevap gelmezse sonsuza kadar dönen spinner yerine dürüst bir mesaj.
  //
  // SÜRE ÖLÇÜLDÜ, tahmin edilmedi: sunucu tarafı 7 ölçümde ortanca 440 ms, en
  // kötü 516 ms; yük 0.79 MB (sıkıştırılmış). Uçtan uca tipik 1-2 sn, yavaş bir
  // mobil bağlantıda 8-10 sn. 20 sn bunun iki katı — sağlıklı bir istekte asla
  // tetiklenmez. Zaten asıl arıza (yanlış backend) aşağıda ANINDA yakalandığı
  // için bu yalnızca gerçek takılmalara karşı emniyet kemeri.
  const FETCH_TIMEOUT_MS = 20000;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  // Veri çek (tümünü çek, filtreleme client-side)
  const fetchData = useCallback(() => {
    if (!connected) return;
    // Timur Bey / DeepBlue AYRI backend'e bağlanıyor ve o backend'ler
    // get_sqlite_history mesajını hiç tanımıyor — istek gönderilse cevap hiç gelmez.
    // Beklemek yerine durumu anında söylüyoruz.
    if (!isMainBackend) {
      setLoading(false);
      setFetchError('backend');
      return;
    }
    setFetchError('');
    setLoading(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setLoading(false);
      setFetchError('timeout');
    }, FETCH_TIMEOUT_MS);
    sendWs({
      type: 'get_sqlite_history',
      payload: { bot_id: 'all', symbol: '', date_from: 0, date_to: 0, limit: 0 },
    });
  }, [connected, isMainBackend, sendWs]);

  useEffect(() => {
    if (!connected) return;
    fetchData();
    sendWs({ type: 'get_lp_trade_tags', payload: {} });   // etiketler + grup listesi (hafif)
  }, [connected]); // eslint-disable-line

  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'sqlite_history') {
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        setFetchError('');
        setTrades(data.payload?.trades || []);
        setFunding(data.payload?.funding_fees || []);
        setCmGrids(data.payload?.cm_grids || []);
        setLoading(false);
      }
      if (data.type === 'lp_trade_tags') {
        setTradeTags(data.payload?.tags || {});
        setLpGroups(data.payload?.groups || []);
      }
      if (data.type === 'lp_group_created') {
        // Yalnız BİZİM başlattığımız akışa cevap ver: /lptrax de aynı mesajı
        // gönderiyor ve o grubun işlemlerimizle ilgisi yok.
        const keys = pendingNewGroupRef.current;
        pendingNewGroupRef.current = null;
        const no = Number(data.payload?.groupNo || 0);
        if (keys && no > 0) {
          assignKeysRef.current(no, keys);
          sendWs({ type: 'get_lp_trade_tags', payload: {} });   // yeni grup listeye girsin
        }
      }
      if (data.type === 'backfill_progress' && data.payload?.status === 'done') {
        setBackfilling(false);
        fetchData();
      }
      if (data.type === 'backfill_progress' && data.payload?.status === 'error') {
        setBackfilling(false);
      }
      if (data.type === 'trades_updated') {
        // GECİKTİR: bu mesaj her botun her senkronunda yayınlanıyor ve her biri
        // 8.9 MB'lık tam çekimi tetikliyordu. Arka arkaya gelenler tek çekimde
        // birleşsin diye 3 sn bekletiliyor — veri yine tam, sadece daha seyrek.
        if (refetchTimer.current) clearTimeout(refetchTimer.current);
        refetchTimer.current = setTimeout(() => { refetchTimer.current = null; fetchData(); }, 3000);
      }
    });
  }, [subscribe, fetchData]);

  // Türetilmiş seçenekler
  const bots = useMemo(() => {
    const ids = new Set<string>();
    trades.forEach(r  => ids.add(r.bot_id));
    funding.forEach(r => ids.add(r.bot_id));
    return Array.from(ids).sort();
  }, [trades, funding]);

  // rawSymbol → borsa adı (ilk eşleşen trades/funding kaydından)
  const symbolOptions = useMemo(() => {
    const rawToExchange = new Map<string, string>();
    trades.forEach(r  => { if (r.symbol && !rawToExchange.has(r.symbol)) rawToExchange.set(r.symbol, botToExchange(r.bot_id)); });
    funding.forEach(r => { if (r.symbol && !rawToExchange.has(r.symbol)) rawToExchange.set(r.symbol, botToExchange(r.bot_id)); });

    // Kaç farklı borsada aynı temel sembol var? (SOL Orderly + SOL Bluefin = 2)
    const baseCount = new Map<string, number>();
    rawToExchange.forEach((_, raw) => {
      const base = fmtSym(raw);
      baseCount.set(base, (baseCount.get(base) ?? 0) + 1);
    });

    return Array.from(rawToExchange.entries())
      .map(([raw, exchange]) => {
        const base = fmtSym(raw);
        return { value: raw, label: (baseCount.get(base) ?? 0) > 1 ? `${base} (${exchange})` : base };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [trades, funding]);

  // Client-side filtreleme
  // "2026-06-03" → new Date("2026-06-03") = UTC midnight (YANLIŞ — Turkey'de +3h kayma)
  // "2026-06-03T00:00:00" → yerel midnight (DOĞRU)
  const fromMs = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime()                    : 0;
  const toMs   = dateTo   ? new Date(dateTo   + 'T00:00:00').getTime() + 86_400_000 - 1  : 0;

  const filteredTrades = useMemo(() =>
    trades.filter(r => {
      if (botFilter  !== 'all' && r.bot_id !== botFilter)           return false;
      if (symFilter.length > 0 && !symFilter.includes(r.symbol))    return false;
      if (sideFilter !== 'all') {
        const buy = isBuySide(r.side);
        if (sideFilter === 'BUY'  && !buy) return false;
        if (sideFilter === 'SELL' &&  buy) return false;
      }
      if (fromMs && (r.executedAtMillis || 0) < fromMs)         return false;
      if (toMs   && (r.executedAtMillis || 0) > toMs)           return false;
      // Atanmışları gizlemek çalışma kümesini küçültür — seçim hangi yöntemle
      // yapılırsa yapılsın kolaylaşır.
      if (onlyUngrouped && (tradeTags[r.bot_id]?.[String(r.id)] || 0) > 0) return false;
      return true;
    }).sort((a, b) => (b.executedAtMillis || 0) - (a.executedAtMillis || 0)),
  [trades, botFilter, symFilter, sideFilter, fromMs, toMs, onlyUngrouped, tradeTags]);

  // Kararlı handler'ların güncel listeye erişmesi için ref. Değer her render'da
  // tazeleniyor ama referans hiç değişmiyor — selectRange böylece sabit kalıyor.
  const filteredRef = useRef<HistoryTrade[]>([]);
  filteredRef.current = filteredTrades;


  const filteredFunding = useMemo(() =>
    funding.filter(r => {
      if (botFilter !== 'all' && r.bot_id !== botFilter)             return false;
      if (symFilter.length > 0 && !symFilter.includes(r.symbol))    return false;
      if (fromMs && (r.createdTime || 0) < fromMs)              return false;
      if (toMs   && (r.createdTime || 0) > toMs)                return false;
      return true;
    }).sort((a, b) => (b.createdTime || 0) - (a.createdTime || 0)),
  [funding, botFilter, symFilter, fromMs, toMs]);

  // ── Artımlı render ──────────────────────────────────────────────────────────
  // 31.874 satırı birden DOM'a basmak ~414.000 düğüm demekti; hem ilk yükleme
  // hem her etkileşim bunun altında eziliyordu. Sabit yükseklikli sanallaştırma
  // yerine artımlı pencere seçildi: satır yüksekliği rozetlere göre değişebildiği
  // için yükseklik VARSAYMAK kaydırmayı zıplatırdı.
  //
  // Dikkat: pencere yalnız BASILAN satırları sınırlar. filteredTrades olduğu gibi
  // duruyor — toplamlar, "tümünü seç", aralık seçimi ve dışa aktarma tam küme
  // üzerinde çalışmaya devam ediyor. Dilim 0'dan başladığı için satır indeksleri
  // de filteredTrades ile birebir aynı kalıyor (selectRange indeksle çalışıyor).
  const ROWS_STEP = 200;
  const [shownTrades,  setShownTrades]  = useState(ROWS_STEP);
  const [shownFunding, setShownFunding] = useState(ROWS_STEP);

  // Filtre/veri değişince pencereyi başa al — oturum boyunca birikip yavaşlamasın.
  useEffect(() => { setShownTrades(ROWS_STEP); }, [botFilter, symFilter, sideFilter, fromMs, toMs, onlyUngrouped, trades]);
  useEffect(() => { setShownFunding(ROWS_STEP); }, [botFilter, symFilter, fromMs, toMs, funding]);

  const tradeSlice   = useMemo(() => filteredTrades.slice(0, shownTrades),   [filteredTrades, shownTrades]);
  const fundingSlice = useMemo(() => filteredFunding.slice(0, shownFunding), [filteredFunding, shownFunding]);

  // Dibe yaklaşınca bir sonraki dilimi ekle. rootMargin sayesinde kullanıcı
  // sınıra varmadan yükleniyor, kesinti hissedilmiyor.
  const moreTradesRef  = useRef<HTMLTableRowElement | null>(null);
  const moreFundingRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    const el = moreTradesRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      es => { if (es[0].isIntersecting) setShownTrades(s => Math.min(s + ROWS_STEP, filteredTrades.length)); },
      { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [filteredTrades.length, shownTrades]);
  useEffect(() => {
    const el = moreFundingRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      es => { if (es[0].isIntersecting) setShownFunding(s => Math.min(s + ROWS_STEP, filteredFunding.length)); },
      { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [filteredFunding.length, shownFunding]);

  // Aktif filtre var mı?
  const hasFilter = dateFrom || dateTo || botFilter !== 'all' || symFilter.length > 0 || sideFilter !== 'all' || onlyUngrouped;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setBotFilter('all');
    setSymFilter([]);
    setSideFilter('all');
    setOnlyUngrouped(false);
  };

  // ── Seçim yardımcıları ──────────────────────────────────────────────────────
  // Aralık seçimi: çapa satırdan tıklanan satıra kadar hepsini SEÇER (toggle etmez).
  // Toggle etseydi ileri-geri sürüklerken durum titrerdi; aralık seçimi "ekle" olmalı.
  const selectRange = useCallback((from: number, to: number) => {
    const [a, b] = from <= to ? [from, to] : [to, from];
    setSelected(prev => {
      const n = new Set(prev);
      // filteredTrades'e REF üzerinden bakıyoruz: değer her zaman güncel, ama
      // callback'in kimliği sabit kalıyor (memo'lu satırlar bozulmasın).
      const list = filteredRef.current;
      for (let i = a; i <= b; i++) {
        const r = list[i];
        if (r) n.add(selKey(r.bot_id, r.id));
      }
      return n;
    });
  }, []);

  // Satıra tıklama: shift ile aralık, düz tıkla toggle.
  // Metin seçerken kazara toggle olmasın diye seçim boş değilse yok sayılır.
  const onRowClick = useCallback((e: React.MouseEvent, idx: number, botId: string, id: string) => {
    // SIRA ÖNEMLİ: shift kontrolü metin-seçimi korumasından ÖNCE gelmeli.
    // Shift+tıklama tarayıcıda zaten metin seçimini genişleten harekettir; önce
    // isCollapsed'a bakınca aralık seçimi hiç çalışmıyordu.
    if (e.shiftKey && lastIdxRef.current != null) {
      window.getSelection()?.removeAllRanges();   // shift'in açtığı istemsiz seçimi temizle
      selectRange(lastIdxRef.current, idx);
      return;
    }
    if (!window.getSelection()?.isCollapsed) return;   // metin seçiliyorsa toggle etme
    lastIdxRef.current = idx;
    toggleSel(botId, id);
  }, [selectRange, toggleSel]);

  // Onay kutusunun kendi handler'ları — satır bileşenine kararlı referans geçsin.
  const onBoxClick = useCallback((e: React.MouseEvent, idx: number) => {
    e.stopPropagation();                    // satır handler'ı çift toggle etmesin
    if (e.shiftKey && lastIdxRef.current != null) {
      e.preventDefault();
      selectRange(lastIdxRef.current, idx);
    }
  }, [selectRange]);

  const onBoxChange = useCallback((idx: number, botId: string, id: string) => {
    lastIdxRef.current = idx;
    toggleSel(botId, id);
  }, [toggleSel]);

  // Görünen kümenin seçim durumu — başlıktaki üç durumlu kutu için.
  // ESKİDEN visibleKeys HER render'da 31.874 elemanlı bir dizi üretiyordu, oysa
  // yalnız "tümünü seç" tıklamasında gerekiyor. Sayım memoize edildi, dizi ise
  // artık hiç kurulmuyor — döngü doğrudan filteredTrades üzerinde. Davranış aynı.
  const visibleSelCnt = useMemo(() => {
    let n = 0;
    for (const r of filteredTrades) if (selected.has(selKey(r.bot_id, r.id))) n++;
    return n;
  }, [filteredTrades, selected]);
  const allVisibleSel = filteredTrades.length > 0 && visibleSelCnt === filteredTrades.length;
  const toggleAllVisible = () => setSelected(prev => {
    const n = new Set(prev);
    for (const r of filteredTrades) {
      const k = selKey(r.bot_id, r.id);
      if (allVisibleSel) n.delete(k); else n.add(k);
    }
    return n;
  });
  // Filtre client-side olduğu için seçim filtre değişince GİZLİ kalabiliyor ve
  // assignGroup doğrudan `selected` üzerinden yazıyor → görünmeyeni de atardı.
  // Sessizce düşürmek yerine sayısını gösteriyoruz; çok adımlı seçim korunsun.
  const hiddenSelCnt = selected.size - visibleSelCnt;

  // Seçimin özeti: sembol bazında AL/SAT ayrı miktar + ağırlıklı ortalama fiyat.
  // Net (al−sat) tek başına yanıltıcı: round-trip kapandığında 0 çıkar ve fiyat
  // bilgisi kaybolur — LP Trax'te aynı tuzağa düşmüştük.
  // Kaynak `trades` (filtrelenmemiş): filtre dışı kalan seçimler de sayılmalı,
  // çünkü gruba atarken onlar da yazılıyor.
  const selStats = useMemo(() => {
    if (selected.size === 0) return [];
    const idx = new Map<string, any>();
    for (const r of trades) idx.set(selKey(r.bot_id, r.id), r);
    const bySym: Record<string, any> = {};
    selected.forEach(k => {
      const r = idx.get(k);
      if (!r) return;
      const e = (bySym[r.symbol] ||= { buyQty: 0, buyNot: 0, sellQty: 0, sellNot: 0, n: 0, fee: 0 });
      const q = Math.abs(Number(r.quantity) || 0), pr = Number(r.price) || 0;
      if (isBuySide(r.side)) { e.buyQty += q; e.buyNot += q * pr; }
      else                   { e.sellQty += q; e.sellNot += q * pr; }
      e.fee += Number(r.fee) || 0;
      e.n += 1;
    });
    return Object.entries(bySym).map(([sym, v]: [string, any]) => ({
      sym, n: v.n, fee: v.fee,
      buyQty: v.buyQty,  buyAvg:  v.buyQty  ? v.buyNot  / v.buyQty  : 0,
      sellQty: v.sellQty, sellAvg: v.sellQty ? v.sellNot / v.sellQty : 0,
      net: v.buyQty - v.sellQty,
    })).sort((a, b) => b.n - a.n);
  }, [selected, trades]);

  // Özet
  const totalPnl     = filteredTrades.reduce((s, r)  => s + (r.realizedPnl ?? 0), 0);
  const totalFunding = filteredFunding.reduce((s, r)  => s + (r.fundingFee  ?? 0), 0);
  const totalFee     = filteredTrades.reduce((s, r)   => s + (r.fee         ?? 0), 0);
  const totalQty     = filteredTrades.reduce((s, r)   => s + (r.quantity    ?? 0), 0);
  const totalVolume  = filteredTrades.reduce((s, r)   => s + (r.price ?? 0) * (r.quantity ?? 0), 0);
  // Gaz komisyondan AYRI: Bluefin'de işlem başına ~$0.03 zincir maliyeti var ve
  // toplamda $22'yi buluyor. Komisyonun içine katmıyoruz (borsa ücreti değil),
  // ama göstermemek maliyeti olduğundan küçük gösteriyordu.
  const totalGas     = filteredTrades.reduce((s, r)   => s + (r.gasFee      ?? 0), 0);

  // ── Ağırlıklı ortalama fiyatlar ────────────────────────────────────────────
  // NEDEN AĞIRLIKLI: düz ortalamada 0,01 HYPE'lık dolum ile 10 HYPE'lık dolum
  // eşit sayılır; market-maker botunda dolum boyutları çok değişken olduğu için
  // çıkan sayı gerçeği yansıtmaz. Doğrusu Σ(fiyat×miktar)/Σmiktar.
  //
  // NEDEN PARİTE (ve gerekirse BOT) BAŞINA: HYPE $42 ile BTC $86.000'i aynı
  // ortalamaya katmak anlamsız bir sayı üretir ve HATA VERMEZ, sadece yanlış
  // görünür. Aynı tuzağın sinsi hâli bot tarafında: bot filtresi "tümü" iken
  // HYPE hem Bluefin-LP'de hem Lighter'da işlem görüyorsa farklı borsaların
  // fiyatları tek ortalamada birleşirdi → o durumda kırılım bot+parite.
  //
  // Komisyon DAHİL DEĞİL: istenen ham fiyat ortalaması. Komisyon zaten şeritte
  // ayrı duruyor, farkın komisyona değip değmediğine kullanıcı kendi karar veriyor.
  const perBotAvg = botFilter === 'all';
  const avgRows = useMemo(() => {
    const m = new Map<string, {
      bot: string; sym: string; buyVol: number; buyQty: number;
      sellVol: number; sellQty: number; n: number;
    }>();
    for (const r of filteredTrades) {
      const key = perBotAvg ? `${r.bot_id}\u0000${r.symbol}` : r.symbol;
      let e = m.get(key);
      if (!e) {
        e = { bot: r.bot_id, sym: r.symbol, buyVol: 0, buyQty: 0, sellVol: 0, sellQty: 0, n: 0 };
        m.set(key, e);
      }
      const px = r.price ?? 0, qty = r.quantity ?? 0;
      if (isBuySide(r.side)) { e.buyVol += px * qty; e.buyQty += qty; }
      else                   { e.sellVol += px * qty; e.sellQty += qty; }
      e.n++;
    }
    return [...m.values()].map(e => ({
      ...e,
      buyAvg:  e.buyQty  > 0 ? e.buyVol  / e.buyQty  : null,
      sellAvg: e.sellQty > 0 ? e.sellVol / e.sellQty : null,
      notional: e.buyVol + e.sellVol,
    })).sort((a, b) => b.notional - a.notional);
  }, [filteredTrades, perBotAvg]);

  // XLSX export
  const handleExport = () => {
    if (tab === 'trades') {
      const header = ['Tarih','Saat','Hesap','Sembol','Yön','Fiyat','Miktar','Hacim (USD)','PnL','Komisyon'];
      const rows   = filteredTrades.map(r => [
        fmtDate(r.executedAtMillis),
        fmtTime(r.executedAtMillis),
        fmtBot(r.bot_id),
        fmtSym(r.symbol),
        r.side,
        r.price,
        r.quantity,
        (r.price || 0) * (r.quantity || 0),
        r.realizedPnl,
        -(r.fee ?? 0),
      ]);
      // Dışa aktarmaya ÖZET de giriyor: raporlamada ham dolum listesinin yanında
      // "hangi pariteyi ortalama kaça aldım/sattım" sorusunun cevabı da olsun.
      const ozet: (string | number)[][] = [
        [], ['ÖZET — ağırlıklı ortalama (Σ fiyat×miktar / Σ miktar, komisyon hariç)'],
        ['Hesap', 'Sembol', 'Ort. Alış', 'Alış Miktar', 'Ort. Satış', 'Satış Miktar', 'Dolum'],
        ...avgRows.map(r => [
          fmtBot(r.bot), fmtSym(r.sym),
          r.buyAvg ?? '', r.buyQty || '',
          r.sellAvg ?? '', r.sellQty || '',
          r.n,
        ]),
      ];
      exportXls([header, ...rows, ...ozet], `islem-gecmisi-${msToDateInput(Date.now())}`);
    } else {
      const header = ['Tarih','Saat','Hesap','Sembol','Tür','Tutar'];
      const rows   = filteredFunding.map(r => [
        fmtDate(r.createdTime),
        fmtTime(r.createdTime),
        fmtBot(r.bot_id),
        fmtSym(r.symbol),
        (r.fundingFee ?? 0) >= 0 ? 'Alındı' : 'Ödendi',
        r.fundingFee,
      ]);
      exportXls([header, ...rows], `funding-gecmisi-${msToDateInput(Date.now())}`);
    }
  };

  const card   = `${t.card} border ${t.cardBorder} rounded-xl`;
  const tabCls = (active: boolean) =>
    `px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
      active ? 'bg-[#4A9FE8] text-[#061530]' : t.btnSecondary
    }`;

  // Filtre dropdown stili (tablo içi)
  const fltSelect = `${FLT} cursor-pointer`;

  const histTabs = (cmTrades.length > 0 || cmFunding.length > 0 || cmGrids.length > 0) ? (
    <div className={`inline-flex rounded-lg border ${t.cardBorder} p-0.5 text-xs`}>
      {([['usd', 'USD'], ['coinm', 'COIN-M']] as const).map(([k, label]) => (
        <button key={k} onClick={() => setHTab(k)}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            hTab === k ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : `${t.muted} hover:opacity-80`}`}>
          {label}{k === 'coinm' && <span className="ml-1 opacity-60">{cmTrades.length}</span>}
        </button>
      ))}
    </div>
  ) : null;

  if (hTab === 'coinm') {
    return (
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Database className={`size-5 ${t.gold}`} />
            <h2 className={`text-xl font-bold ${t.text}`}>Geçmiş — COIN-M</h2>
          </div>
          {histTabs}
        </div>
        <CoinMHistory t={t} trades={cmTrades} funding={cmFunding} grids={cmGrids} fromMs={fromMs} toMs={toMs} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">

      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Database className={`size-5 ${t.gold}`} />
          <h2 className={`text-xl font-bold ${t.text}`}>Geçmiş — Tüm Robotlar</h2>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${t.muted} ${t.cardBorder}`}>SQLite</span>
          {histTabs}
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 text-xs ${t.muted}`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
            {connected ? 'Canlı' : 'Bağlantı bekleniyor…'}
          </div>
          <button
            onClick={() => {
              if (!connected) return;
              setBackfilling(true);
              sendWs({ type: 'run_backfill', payload: { start_t: 1772496000000 } });
            }}
            disabled={backfilling || !connected}
            title="3 Mart 2026'dan itibaren tüm işlemleri SQLite'a aktar"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-blue-500/60 text-blue-400 hover:bg-blue-500/10 disabled:opacity-40 transition-colors`}
          >
            <DatabaseZap className={`size-3.5 ${backfilling ? 'animate-pulse' : ''}`} />
            {backfilling ? 'Aktarılıyor…' : 'Backfill'}
          </button>
          <button
            onClick={handleExport}
            disabled={loading || !connected}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/10 disabled:opacity-40 transition-colors`}
          >
            <Download className="size-3.5" /> Excel
          </button>
          <button
            onClick={fetchData}
            disabled={loading || !connected}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.btnSecondary} disabled:opacity-40`}
          >
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>
      </div>

      {/* Cevap gelmediğinde sessizce dönen spinner yerine SEBEBİ söyle. Eskiden
          ayrı backend'e bağlı bir robot seçiliyken sayfa sonsuza kadar
          "Yükleniyor…" gösteriyordu — yavaşlık sanılıyordu, oysa cevap hiç
          gelmiyordu. */}
      {fetchError && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[11px] flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 mt-px shrink-0" />
          <div className="flex-1">
            {fetchError === 'backend' ? (
              <>
                <span className="text-amber-300 font-semibold">Bu robot ayrı bir sunucu kullanıyor.</span>{' '}
                <span className={t.muted}>
                  Timur Bey ve DeepBlue kendi backend'lerine bağlanıyor; SQLite geçmişi orada tutulmuyor.
                  Bu sayfa zaten <b>tüm robotların</b> geçmişini gösteriyor — ana sunucuda çalışan bir robota
                  (AsterDex, Binance, Extended, Bluefin…) geçersen bu robotun işlemleri de listede olacak.
                </span>
              </>
            ) : (
              <>
                <span className="text-amber-300 font-semibold">
                  Geçmiş {Math.round(FETCH_TIMEOUT_MS / 1000)} saniyede yüklenemedi.
                </span>{' '}
                <span className={t.muted}>Bağlantı yavaş olabilir ya da sunucu yanıt vermiyor.</span>
              </>
            )}
          </div>
          <button onClick={fetchData}
            className="shrink-0 px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/15">
            Tekrar dene
          </button>
        </div>
      )}

      {/* Özet kartlar — Win Rate kaldırıldı */}
      <div className={`grid gap-4 ${totalGas > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
        {[
          { label: 'Toplam PnL',      value: `${totalPnl >= 0 ? '+' : ''}$${totalPnl.toFixed(4)}`,         clr: totalPnl     >= 0 ? t.positive : t.negative },
          { label: 'Toplam Funding',  value: `${totalFunding >= 0 ? '+' : ''}$${totalFunding.toFixed(6)}`,   clr: totalFunding >= 0 ? t.positive : t.negative },
          { label: 'Toplam Komisyon', value: `-$${totalFee.toFixed(6)}`,                                     clr: t.negative },
          ...(totalGas > 0 ? [{ label: 'Toplam Gaz', value: `-$${totalGas.toFixed(4)}`, clr: t.negative }] : []),
        ].map(c => (
          <div key={c.label} className={`${card} p-4`}>
            <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
            <p className={`text-lg font-bold font-mono ${c.clr}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sayım */}
      <div className={`text-xs ${t.muted} flex gap-4`}>
        <span>{filteredTrades.length} işlem</span>
        <span>{filteredFunding.length} funding kaydı</span>
        <span>{bots.length} robot</span>
      </div>

      {/* Funding arbitrage: seçim çubuğu — YAPIŞKAN.
          Liste uzun; dipte 50 satır seçtikten sonra butona ulaşmak için yukarı
          kaydırmak gerekiyordu. */}
      {tab === 'trades' && selected.size > 0 && (
        <div className="sticky bottom-3 z-40 px-3 py-2 rounded-lg border border-sky-500/40 bg-[#0a2560] shadow-2xl">
          {/* Seçimin miktar/ortalama fiyat özeti */}
          {selStats.length > 0 && (
            <div className="flex flex-col gap-0.5 mb-2 pb-2 border-b border-[#164a9e]">
              {selStats.slice(0, 4).map(v => {
                const nq = (x: number) => x.toLocaleString('en-US', { maximumFractionDigits: 4 });
                const np = (x: number) => x >= 1
                  ? x.toLocaleString('en-US', { maximumFractionDigits: 4 })
                  : parseFloat(x.toPrecision(5)).toString();
                return (
                  <div key={v.sym} className="flex items-center gap-3 text-[11px] flex-wrap">
                    <span className="font-semibold text-blue-50 w-24 shrink-0">{v.sym}</span>
                    {v.buyQty > 0 && (
                      <span className="tabular-nums">
                        <span className="text-emerald-400 font-semibold">AL</span>{' '}
                        {nq(v.buyQty)} <span className="text-blue-300/60">@</span> {np(v.buyAvg)}
                      </span>
                    )}
                    {v.sellQty > 0 && (
                      <span className="tabular-nums">
                        <span className="text-rose-400 font-semibold">SAT</span>{' '}
                        {nq(v.sellQty)} <span className="text-blue-300/60">@</span> {np(v.sellAvg)}
                      </span>
                    )}
                    <span className={`tabular-nums ${Math.abs(v.net) < 1e-9 ? 'text-blue-300/60' : 'text-amber-400'}`}
                      title={Math.abs(v.net) < 1e-9 ? 'al = sat, round-trip kapalı' : 'al − sat farkı: açık kalan miktar'}>
                      net {v.net > 0 ? '+' : ''}{nq(v.net)}
                    </span>
                    <span className="ml-auto text-blue-300/60 tabular-nums shrink-0">{v.n} işlem</span>
                  </div>
                );
              })}
              {selStats.length > 4 && (
                <div className="text-[11px] text-blue-300/60">+{selStats.length - 4} sembol daha</div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-sky-300">{selected.size} işlem seçildi</span>
          {hiddenSelCnt > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
              title="Bu seçimler mevcut filtrenin dışında kaldı ama hâlâ seçili — gruba atarken bunlar da yazılır.">
              {hiddenSelCnt}'i görünmüyor
            </span>
          )}
          <div className="relative">
            <button onClick={() => setTagOpen(o => !o)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#4A9FE8] text-[#061530] hover:brightness-110 transition">
              Gruba ata
            </button>
            {tagOpen && (
              <div className="absolute left-0 top-full mt-1.5 z-50 min-w-[190px] rounded-lg border border-[#164a9e] bg-[#0a2560] shadow-2xl p-1.5">
                <button onClick={assignToNewGroup}
                  className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded text-xs font-semibold text-emerald-300 hover:bg-emerald-500/15 border border-emerald-500/30 transition">
                  <span className="text-sm leading-none">+</span>
                  Yeni gruba ata
                  <span className="ml-auto text-[10px] font-normal text-emerald-300/60">{selected.size} işlem</span>
                </button>
                <div className="text-[10px] text-blue-300/70 px-2 pt-2 pb-1">ya da mevcut gruba:</div>
                <div className="max-h-72 overflow-y-auto">
                  {([['sui', 'Sui cüzdanı'], ['solana', 'Sol cüzdanı'], ['arc', 'Arc cüzdanı'], ['perp', 'Perps']] as const).map(([k, label]) => {
                    // Bir grup içerdiği HER zincirde listelenir (karma grup ikisinde de görünür).
                    // KAPALI gruplar listelenmiyor: kapanmış bir gruba yeni işlem
                    // eklemek muhasebeyi geriye dönük bozar, menüde yer kaplamaktan
                    // başka işe yaramıyordu.
                    const gs = lpGroups.filter(g => g.status !== 'closed' && (k === 'perp'
                      ? (g.kind === 'perp' || (!g.positions && g.trades > 0))
                      : (g.chains?.[k] || 0) > 0));
                    return (
                      <div key={k}>
                        <div className="text-[10px] font-semibold text-blue-400/80 px-2 pt-2 pb-1 uppercase tracking-wide">{label}</div>
                        {gs.length === 0 && <div className="px-2 pb-1 text-[10px] text-blue-300/40">grup yok</div>}
                        {gs.map(g => (
                          <button key={g.no} onClick={() => assignGroup(g.no)}
                            className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded text-xs text-blue-100 hover:bg-[#1e4a8f] transition">
                            {/* Grup numarası artık CÜZDAN İÇİ sıra (global no iç anahtar).
                                Zincir etiketi cüzdanlar arası ayrımı görünür kılar. */}
                            <span className="font-semibold">
                              {g.kind === 'solana' ? 'Solana' : g.kind === 'sui' ? 'Sui' : g.kind === 'arc' ? 'Arc' : 'Perp'} · Grup {g.rank || g.no}
                            </span>
                            <span className="text-[10px] text-blue-300/70">
                              {k !== 'perp' && (g.chains?.[k] || 0) > 0 ? `${g.chains![k]} poz` : (g.positions ? `${g.positions} poz` : '')}
                              {g.trades ? `${((k !== 'perp' && g.chains?.[k]) || g.positions) ? ' · ' : ''}${g.trades} işlem` : ''}
                              {/* karma grup: diğer zinciri de belirt */}
                              {k !== 'perp' && g.chains && Object.keys(g.chains).length > 1 ? ' · karma' : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => assignGroup(0)}
                  className="w-full text-left px-2 py-1.5 mt-1 rounded text-xs text-rose-300 hover:bg-rose-500/20 border-t border-[#164a9e] transition">
                  Bağı kaldır
                </button>
              </div>
            )}
          </div>
          <button onClick={() => setSelected(new Set())}
            className="px-3 py-1.5 rounded-lg text-xs border border-blue-700/60 text-blue-300 hover:bg-[#1e1040] transition">
            Seçimi temizle
          </button>
          </div>
        </div>
      )}

      {/* Tab seçici + tarih filtresi + temizle */}
      <div className="flex items-center gap-2 flex-wrap">
        <button className={tabCls(tab === 'trades')}  onClick={() => setTab('trades')}>
          İşlem Geçmişi ({filteredTrades.length})
        </button>
        <button className={tabCls(tab === 'funding')} onClick={() => setTab('funding')}>
          Funding Geçmişi ({filteredFunding.length})
        </button>

        {/* Tarih seçici */}
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onApply={(f, t) => { setDateFrom(f); setDateTo(t); }}
        />

        {/* Gruplanmamışlar — atanmışları gizleyip çalışma kümesini küçültür */}
        {tab === 'trades' && (
          <button
            onClick={() => setOnlyUngrouped(v => !v)}
            title="Zaten bir gruba atanmış işlemleri gizle"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              onlyUngrouped
                ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                : 'border-[#164a9e] text-blue-300/70 hover:text-blue-100'}`}
          >
            Sadece gruplanmamışlar
          </button>
        )}

        {hasFilter && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-rose-500/60 text-rose-400 hover:bg-rose-500/15 transition-colors ml-auto"
          >
            <FilterX className="size-3.5" /> Filtreleri Temizle
          </button>
        )}
      </div>

      {/* ── İşlem Geçmişi ── */}
      {tab === 'trades' && (
        <div className={`${card} overflow-hidden`}>
          <button
            onClick={() => setTradesOpen(v => !v)}
            className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 transition-opacity ${tradesOpen ? `border-b ${t.cardBorder}` : ''}`}
          >
            <div className="flex items-center gap-3">
              <span className={t.muted}>{tradesOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</span>
              <h3 className={`text-sm font-semibold ${t.text}`}>İşlemler</h3>
            </div>
            <div className="flex items-center gap-5 text-xs">
              <span className={t.muted}>
                Miktar: <span className={`font-bold font-mono ${t.text}`}>{totalQty.toFixed(4)}</span>
              </span>
              <span className={t.muted}>
                PnL: <span className={`font-bold font-mono ${totalPnl >= 0 ? t.positive : t.negative}`}>
                  {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(4)}
                </span>
              </span>
              <span className={t.muted}>
                Komisyon: <span className={`font-bold font-mono ${t.negative}`}>-${totalFee.toFixed(4)}</span>
              </span>
            </div>
          </button>

          {/* ── Ortalamalar ─────────────────────────────────────────────────────
              Düğme KOŞULSUZ: filtreye göre bazen sayı bazen düğme göstermek
              şeridin şeklini oynatır ve kullanıcı sayıyı nerede arayacağını
              bilemez. Hep aynı yerde, hep aynı davranış; panel şeridin altında
              açılıyor, yani mevcut düzen hiç bozulmuyor. */}
          {filteredTrades.length > 0 && (
            <div className={`px-4 py-1.5 border-b ${t.cardBorder} flex items-center gap-3 flex-wrap`}>
              <button
                onClick={() => setShowAvg(v => !v)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${
                  showAvg ? `${t.gold} border-current` : `${t.muted} ${t.cardBorder} hover:opacity-80`}`}>
                <Sigma size={12} />
                Ortalamalar
                <span className={showAvg ? '' : 'opacity-60'}>
                  {showAvg ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                </span>
              </button>
              <span className={`text-[10px] ${t.muted}`}>
                ağırlıklı · {avgRows.length} {perBotAvg ? 'bot-parite' : 'parite'}
              </span>
            </div>
          )}

          {showAvg && filteredTrades.length > 0 && (
            <div className={`px-4 py-3 border-b ${t.cardBorder}`}>
              <table className="w-full text-[11px]">
                <thead>
                  <tr className={`${t.muted} border-b ${t.cardBorder}`}>
                    {perBotAvg && <th className="px-2 py-1.5 text-left font-medium">Hesap</th>}
                    <th className="px-2 py-1.5 text-left font-medium">Parite</th>
                    <th className="px-2 py-1.5 text-right font-medium">Ort. Alış</th>
                    <th className="px-2 py-1.5 text-right font-medium">Ort. Satış</th>
                    <th className="px-2 py-1.5 text-right font-medium opacity-60">Fark</th>
                    <th className="px-2 py-1.5 text-right font-medium">Dolum</th>
                  </tr>
                </thead>
                <tbody>
                  {avgRows.map(r => {
                    const fark = (r.buyAvg !== null && r.sellAvg !== null) ? r.sellAvg - r.buyAvg : null;
                    const farkPct = (fark !== null && r.buyAvg) ? (fark / r.buyAvg) * 100 : null;
                    return (
                      <tr key={`${r.bot}-${r.sym}`} className={`border-b ${t.tableRow}`}>
                        {perBotAvg && <td className={`px-2 py-1.5 ${t.muted}`}>{fmtBot(r.bot)}</td>}
                        <td className={`px-2 py-1.5 font-semibold ${t.gold}`}>{fmtSym(r.sym)}</td>
                        {/* Miktar ortalamanın YANINDA: alış ve satış miktarı eşit
                            değilse iki ortalama farklı şeyleri anlatır ve farkları
                            "gerçekleşmiş kâr" değildir — dengesizlik gözle görünsün. */}
                        <td className="px-2 py-1.5 text-right font-mono">
                          <span className={t.text}>{fmtAvgPrice(r.buyAvg)}</span>
                          {r.buyQty > 0 && <span className={`ml-1.5 text-[10px] ${t.muted}`}>{fmtAvgQty(r.buyQty)}</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">
                          <span className={t.text}>{fmtAvgPrice(r.sellAvg)}</span>
                          {r.sellQty > 0 && <span className={`ml-1.5 text-[10px] ${t.muted}`}>{fmtAvgQty(r.sellQty)}</span>}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-mono text-[10px] ${t.muted} opacity-70`}>
                          {fark === null ? '—'
                            : `${fark >= 0 ? '+' : ''}${fmtAvgPrice(Math.abs(fark)).replace('$', fark < 0 ? '-$' : '$')}` +
                              (farkPct !== null ? ` (${farkPct >= 0 ? '+' : ''}${farkPct.toFixed(2)}%)` : '')}
                        </td>
                        <td className={`px-2 py-1.5 text-right font-mono ${t.muted}`}>{r.n}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tradesOpen && (
            filteredTrades.length === 0 && !loading ? (
              <div className={`p-10 text-center text-xs ${t.muted}`}>
                {loading ? 'Yükleniyor…' : 'Kayıt yok.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    {/* ── Başlık satırı ── */}
                    <tr className={`${t.tableHeader} border-b ${t.cardBorder}`}>
                      <th className="px-2 py-2.5 w-8">
                        {/* Yalnız GÖRÜNENLERİ kapsar: eskiden kaldırınca new Set() ile
                            filtre dışındaki seçimler de siliniyordu. Üç durumlu. */}
                        <input type="checkbox" className="accent-sky-500"
                          title={allVisibleSel ? 'Görünenlerin seçimini kaldır' : 'Görünen tüm işlemleri seç'}
                          checked={allVisibleSel}
                          ref={el => { if (el) el.indeterminate = visibleSelCnt > 0 && !allVisibleSel; }}
                          onChange={toggleAllVisible} />
                      </th>
                      <th className="px-2 py-2.5 font-medium text-left">Grup</th>
                      <th className="px-3 py-2.5 font-medium text-left">Tarih</th>
                      <th className="px-3 py-2.5 font-medium text-left">Saat</th>
                      <th className="px-3 py-2.5 font-medium text-left">Hesap</th>
                      <th className="px-3 py-2.5 font-medium text-left">Sembol</th>
                      <th className="px-3 py-2.5 font-medium text-left">Yön</th>
                      <th className="px-3 py-2.5 font-medium text-right">Fiyat</th>
                      <th className="px-3 py-2.5 font-medium text-right">Miktar</th>
                      <th className="px-3 py-2.5 font-medium text-right">Hacim (USD)</th>
                      <th className="px-3 py-2.5 font-medium text-right">PnL</th>
                      <th className="px-3 py-2.5 font-medium text-right">Komisyon</th>
                    </tr>
                    {/* ── Filtre satırı ── */}
                    <tr className={`border-b ${t.cardBorder} bg-[#030d1f]/60`}>
                      <td /><td /><td /><td />
                      {/* Hesap */}
                      <td className="px-2 py-1.5">
                        <select value={botFilter} onChange={e => setBotFilter(e.target.value)} className={fltSelect}>
                          <option value="all">Tümü</option>
                          {bots.map(id => <option key={id} value={id}>{fmtBot(id)}</option>)}
                        </select>
                      </td>
                      {/* Sembol */}
                      <td className="px-2 py-1.5">
                        <SymbolMultiSelect
                          options={symbolOptions}
                          selected={symFilter}
                          onChange={setSymFilter}
                        />
                      </td>
                      {/* Yön */}
                      <td className="px-2 py-1.5">
                        <select value={sideFilter} onChange={e => setSideFilter(e.target.value)} className={fltSelect}>
                          <option value="all">Tümü</option>
                          <option value="BUY">BUY</option>
                          <option value="SELL">SELL</option>
                        </select>
                      </td>
                      <td /><td /><td /><td /><td />
                    </tr>
                  </thead>
                  <tbody>
                    {loading && filteredTrades.length === 0 ? (
                      <tr><td colSpan={12} className={`px-3 py-8 text-center ${t.muted}`}>Yükleniyor…</td></tr>
                    ) : (
                      <>
                        {tradeSlice.map((r, idx) => (
                          <TradeRow
                            key={selKey(r.bot_id, r.id)}
                            r={r} idx={idx} t={t}
                            sel={selected.has(selKey(r.bot_id, r.id))}
                            tag={tagOf(r.bot_id, r.id)}
                            onRowClick={onRowClick}
                            onBoxClick={onBoxClick}
                            onBoxChange={onBoxChange} />
                        ))}
                        {/* Sınıra yaklaşınca bir sonraki dilimi tetikleyen satır */}
                        {tradeSlice.length < filteredTrades.length && (
                          <tr ref={moreTradesRef}>
                            <td colSpan={12} className={`px-3 py-3 text-center text-[11px] ${t.muted}`}>
                              {filteredTrades.length - tradeSlice.length} kayıt daha yükleniyor…
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                  {filteredTrades.length > 0 && (
                    <tfoot>
                      <tr className={`border-t-2 ${t.cardBorder} bg-[#0a1628]/60`}>
                        <td colSpan={7} className={`px-3 py-2.5 text-xs font-semibold ${t.muted}`}>
                          TOPLAM — {filteredTrades.length} işlem
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs ${t.muted}`} />
                        <td className={`px-3 py-2.5 text-right text-xs font-bold font-mono ${t.text}`}>
                          {totalQty.toFixed(4)}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-bold font-mono ${t.muted}`}>
                          ${totalVolume.toFixed(2)}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-bold font-mono ${totalPnl >= 0 ? t.positive : t.negative}`}>
                          {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(4)}
                        </td>
                        <td className={`px-3 py-2.5 text-right text-xs font-bold font-mono ${t.negative}`}>
                          -${totalFee.toFixed(6)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* ── Funding Geçmişi ── */}
      {tab === 'funding' && (
        <div className={`${card} overflow-hidden`}>
          <button
            onClick={() => setFundingOpen(v => !v)}
            className={`w-full px-4 py-3 flex items-center justify-between hover:opacity-80 transition-opacity ${fundingOpen ? `border-b ${t.cardBorder}` : ''}`}
          >
            <div className="flex items-center gap-3">
              <span className={t.muted}>{fundingOpen ? <ChevronDown size={15}/> : <ChevronRight size={15}/>}</span>
              <h3 className={`text-sm font-semibold ${t.text}`}>Funding Ücretleri</h3>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className={t.muted}>Toplam Funding:</span>
              <span className={`font-bold font-mono ${totalFunding >= 0 ? t.positive : t.negative}`}>
                {totalFunding >= 0 ? '+' : ''}${totalFunding.toFixed(6)}
              </span>
            </div>
          </button>

          {fundingOpen && (
            filteredFunding.length === 0 && !loading ? (
              <div className={`p-10 text-center text-xs ${t.muted}`}>Kayıt yok.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    {/* ── Başlık satırı ── */}
                    <tr className={`${t.tableHeader} border-b ${t.cardBorder}`}>
                      <th className="px-3 py-2.5 font-medium text-left">Tarih</th>
                      <th className="px-3 py-2.5 font-medium text-left">Saat</th>
                      <th className="px-3 py-2.5 font-medium text-left">Hesap</th>
                      <th className="px-3 py-2.5 font-medium text-left">Sembol</th>
                      <th className="px-3 py-2.5 font-medium text-left">Tür</th>
                      <th className="px-3 py-2.5 font-medium text-right">Tutar</th>
                    </tr>
                    {/* ── Filtre satırı ── */}
                    <tr className={`border-b ${t.cardBorder} bg-[#030d1f]/60`}>
                      <td /><td />
                      <td className="px-2 py-1.5">
                        <select value={botFilter} onChange={e => setBotFilter(e.target.value)} className={fltSelect}>
                          <option value="all">Tümü</option>
                          {bots.map(id => <option key={id} value={id}>{fmtBot(id)}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <SymbolMultiSelect
                          options={symbolOptions}
                          selected={symFilter}
                          onChange={setSymFilter}
                        />
                      </td>
                      <td /><td />
                    </tr>
                  </thead>
                  <tbody>
                    {loading && filteredFunding.length === 0 ? (
                      <tr><td colSpan={6} className={`px-3 py-8 text-center ${t.muted}`}>Yükleniyor…</td></tr>
                    ) : (
                      <>
                        {fundingSlice.map((r, idx) => {
                          const received = (r.fundingFee ?? 0) >= 0;
                          return (
                            <tr key={r.id || idx} className={`border-b ${t.tableRow} ${t.cardBorder}`}>
                              <td className={`px-3 py-2 ${t.muted}`}>{fmtDate(r.createdTime)}</td>
                              <td className={`px-3 py-2 ${t.muted}`}>{fmtTime(r.createdTime)}</td>
                              <td className="px-3 py-2">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#0a2560] text-blue-300">
                                  {fmtBot(r.bot_id)}
                                </span>
                              </td>
                              <td className={`px-3 py-2 font-semibold ${t.gold}`}>{fmtSym(r.symbol)}</td>
                              <td className="px-3 py-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  received ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                                }`}>{received ? 'Alındı' : 'Ödendi'}</span>
                              </td>
                              <td className={`px-3 py-2 text-right font-mono font-bold ${received ? t.positive : t.negative}`}>
                                {received ? '+$' : '-$'}{Math.abs(r.fundingFee ?? 0).toFixed(6)}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Sınıra yaklaşınca bir sonraki dilimi tetikleyen satır */}
                        {fundingSlice.length < filteredFunding.length && (
                          <tr ref={moreFundingRef}>
                            <td colSpan={6} className={`px-3 py-3 text-center text-[11px] ${t.muted}`}>
                              {filteredFunding.length - fundingSlice.length} kayıt daha yükleniyor…
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
